import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, MapPin, ChevronUp } from './icons';
import { formatUbicacion } from '../utils/locationUtils';

const LocationSelector = ({ value, onChange, placeholder = "Seleccionar ubicación", required = false, className = "" }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isRegionExpanded, setIsRegionExpanded] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const ubicacionesPrincipales = [
        "TECNOBOGA - AVENIDA DEL VALLE NORTE #765 - HUECHURABA",
        "VICSA - AVENIDA DEL VALLE NORTE #787 PISO 5 – HUECHURABA",
        "B2B - AVENIDA DEL VALLE NORTE #787 PISO 6 Of. 604/602 - HUECHURABA",
        "B2B- TB- VICSA  - PUERTO SANTIAGO #259 - PUDAHUEL",
        "BUNZL - DPS - AVENIDA DEL VALLE NORTE #841 PISO 5 – HUECHURABA",
        "B2B TIENDA PANAMERICANA - CONCHALÍ",
        "B2B TIENDA LA FABRICA - SAN JOAQUIN",
        "B2B TIENDA VESPUCIO - CONCHALI",
        "B2B TIENDA MAIPU - MAIPÚ",
        "B2B TIENDA PUDAHUEL - PUDAHUEL",
        "B2B TIENDA PUERTO MONTT - PUERTO MONTT"
    ];

    const ubicacionesRegion = [
        "Argentina",
        "Uruguay",
        "Antofagasta",
        "Concepción",
        "Iquique",
        "La Serena",
        "Pucón",
        "Temuco"
    ];

    const handleLocationSelect = (location) => {
        if (location && typeof location === 'string') {
            onChange(location);
        }
    };

    const isRegionLocation = ubicacionesRegion.includes(value);

    // Calcular posición del dropdown
    const updateDropdownPosition = () => {
        if (isDropdownOpen && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: buttonRect.bottom + window.scrollY,
                left: buttonRect.left + window.scrollX,
                width: buttonRect.width
            });
        }
    };

    useEffect(() => {
        if (isDropdownOpen) {
            updateDropdownPosition();

            // Actualizar posición en scroll y resize
            const handleScroll = () => updateDropdownPosition();
            const handleResize = () => updateDropdownPosition();

            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isDropdownOpen]);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                setIsRegionExpanded(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen]);

    return (
        <div className={`relative ${className}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ubicación {required && <span className="text-red-500">*</span>}
            </label>

            <div className="relative" style={{ zIndex: isDropdownOpen ? 9999 : 'auto' }}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-4 w-4 text-gray-400" />
                </div>

                <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full pl-10 pr-10 py-2.5 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                >
                    <span className={value ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                        {formatUbicacion(value) || placeholder}
                    </span>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        {isDropdownOpen ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                    </div>
                </button>
            </div>

            {isDropdownOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        minWidth: '300px'
                    }}
                >
                    {/* Ubicaciones Principales */}
                    <div className="p-2">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                            Ubicaciones Principales
                        </div>
                        {ubicacionesPrincipales.map((location) => (
                            <button
                                key={location}
                                type="button"
                                onClick={() => {
                                    handleLocationSelect(location);
                                    setIsDropdownOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 border-b border-gray-100 dark:border-gray-600 ${value === location ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                    <span className="text-sm">{location}</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Separador */}
                    <div className="border-t border-gray-200 dark:border-gray-600">
                        <div className="p-2">
                            <button
                                type="button"
                                onClick={() => setIsRegionExpanded(!isRegionExpanded)}
                                className="flex items-center justify-between w-full text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded py-1"
                            >
                                <span>Región</span>
                                {isRegionExpanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                ) : (
                                    <ChevronDown className="w-4 h-4" />
                                )}
                            </button>
                            {isRegionExpanded && ubicacionesRegion.map((location) => (
                                <button
                                    key={location}
                                    type="button"
                                    onClick={() => {
                                        handleLocationSelect(location);
                                        setIsDropdownOpen(false);
                                    }}
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${value === location ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                                        }`}
                                >
                                    <div className="flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                        <span className="text-sm">{location}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Mostrar indicador si la ubicación seleccionada es de región */}
            {isRegionLocation && (
                <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    Ubicación de región
                </div>
            )}
        </div>
    );
};

export default LocationSelector; 
